import { displayPrice } from '../../util/configHelpers';
import { formatMoney } from '../../util/currency';
import { hasNegotiablePrice } from '../../util/negotiablePrice';
import { createSlug } from '../../util/urlHelpers';
import { pathByRouteName } from '../../util/routes';
import { buildListingBreadcrumbs } from '../../util/racingThemeListingBreadcrumbs';
import { resolveLatestProcessName } from '../../transactions/transaction';
import {
  findCategoryName,
  resolveDepartment,
} from '../../util/racingThemeDepartment';
import {
  getFieldValue,
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
} from '../../util/fieldHelpers';
import { SCHEMA_TYPE_TEXT } from '../../util/types';
import { mapUserToMemberIdentity } from '../RacingThemeMemberIdentity/mapUserToMemberIdentity';

const PLACEHOLDER_IMAGE =
  'data:image/svg+xml;charset=UTF-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="960" height="720" viewBox="0 0 960 720"%3E%3Crect fill="%23DDE2E6" width="960" height="720"/%3E%3C/svg%3E';

const TEXT_ACCORDION_ICONS = {
  lf_how_it_works: 'howItWorks',
  lf_listing_terms_conditions: 'terms',
  lf_booking_requirements: 'requirements',
  lf_exposure: 'exposure',
};

/**
 * Build accordion rows for listing text fields (category-filtered).
 * @param {Object} publicData
 * @param {Object} metadata
 * @param {Array} listingFieldConfigs
 * @param {Object} categoryConfiguration
 * @returns {Array}
 */
const buildTextFieldAccordions = (
  publicData,
  metadata,
  listingFieldConfigs,
  categoryConfiguration
) => {
  if (!listingFieldConfigs?.length || !categoryConfiguration) {
    return [];
  }

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);
  const listingType = publicData?.listingType;

  return listingFieldConfigs.reduce((acc, config) => {
    if (config.schemaType !== SCHEMA_TYPE_TEXT) {
      return acc;
    }
    if (config.showConfig?.displayOnListingPage === false) {
      return acc;
    }
    if (!isFieldForListingType(listingType, config)) {
      return acc;
    }
    if (!isFieldForCategory(currentCategories, config)) {
      return acc;
    }

    const scope = config.scope || 'public';
    const value =
      scope === 'public'
        ? getFieldValue(publicData, config.key)
        : scope === 'metadata'
        ? getFieldValue(metadata, config.key)
        : null;
    if (!value) {
      return acc;
    }

    const title = config.showConfig?.label || config.label || config.key;
    acc.push({
      id: config.key,
      title,
      icon: TEXT_ACCORDION_ICONS[config.key] || 'details',
      body: value,
    });
    return acc;
  }, []);
};

const getImageUrl = (image, preferredKeys = []) => {
  const variants = image?.attributes?.variants || {};
  for (const key of preferredKeys) {
    if (variants[key]?.url) {
      return variants[key].url;
    }
  }
  const first = Object.values(variants).find(v => v?.url);
  return first?.url || null;
};

/**
 * Map process + category to Racing Theme listing channel variant.
 * @param {string} processName
 * @param {Object} publicData
 * @param {Array} categories
 * @returns {'shop'|'book'|'partner'}
 */
export const resolveListingVariant = (processName, publicData = {}, categories = []) =>
  resolveDepartment(publicData, categories, processName);

const channelLabel = variant => {
  if (variant === 'book') {
    return 'Book';
  }
  if (variant === 'partner') {
    return 'Partner';
  }
  return 'Shop';
};

const channelHref = variant => `/p/${variant}`;

/**
 * Map Sharetribe listing (+ reviews) to RacingThemeListingPage content DTO.
 * CTA panels are not included — live OrderPanel is slotted separately.
 *
 * @param {Object} listing
 * @param {Object} params
 * @returns {Object}
 */
export const mapListingToRacingThemeContent = (
  listing,
  { reviews = [], config, intl, processType, authorDisplayName, routeConfiguration }
) => {
  const { title = '', description = '', price, publicData = {} } =
    listing?.attributes || {};
  const categories = config?.categoryConfiguration?.categories || [];
  const listingTypes = config?.listing?.listingTypes || [];
  const listingType = publicData.listingType;
  const listingTypeConfig = listingTypes.find(conf => conf.listingType === listingType);

  const processName =
    processType ||
    resolveLatestProcessName(publicData.transactionProcessAlias?.split('/')?.[0]) ||
    '';
  const variant = resolveListingVariant(processName, publicData, categories);

  const level1 = publicData.categoryLevel1;
  const level2 = publicData.categoryLevel2;
  const level3 = publicData.categoryLevel3;
  const cat1 = findCategoryName(categories, level1);
  const cat2 = findCategoryName(categories, level2);
  const cat3 = findCategoryName(categories, level3);

  const breadcrumbs = routeConfiguration
    ? buildListingBreadcrumbs(routeConfiguration, {
        variant,
        channelLabel: channelLabel(variant),
        level1Id: level1,
        level2Id: level2,
        level3Id: level3,
        cat1Name: cat1,
        cat2Name: cat2,
        cat3Name: cat3,
        currentLabel: authorDisplayName || title,
      })
    : [
        { label: 'Home', href: '/' },
        { label: channelLabel(variant), href: channelHref(variant) },
        ...(cat1 && cat1.toLowerCase() !== channelLabel(variant).toLowerCase()
          ? [{ label: cat1, href: '#' }]
          : []),
        ...(cat2 ? [{ label: cat2, href: '#' }] : []),
        ...(cat3 ? [{ label: cat3, href: '#' }] : []),
        { label: authorDisplayName || title, href: null },
      ];

  const author = listing?.author;
  const memberIdentity = mapUserToMemberIdentity(author, {
    config,
    fallbackName: authorDisplayName || title || 'Seller',
  });

  const variantPrefix = config?.layout?.listingImage?.variantPrefix || 'listing-card';
  const galleryItems = (listing?.images || []).map((image, index) => {
    const src =
      getImageUrl(image, [
        'scaled-large',
        'scaled-medium',
        `${variantPrefix}-4x`,
        `${variantPrefix}-2x`,
        variantPrefix,
      ]) || PLACEHOLDER_IMAGE;
    const thumbSrc =
      getImageUrl(image, [`${variantPrefix}`, `${variantPrefix}-2x`, 'scaled-small', 'scaled-medium']) ||
      src;
    return {
      id: image?.id?.uuid || `img-${index}`,
      type: 'image',
      src,
      thumbSrc,
      alt: title || `Listing image ${index + 1}`,
    };
  });

  const showPrice = displayPrice(listingTypeConfig);
  let priceLabel = '';
  if (hasNegotiablePrice(publicData) && intl) {
    priceLabel = intl.formatMessage({ id: 'ListingCard.priceNegotiable' });
  } else if (showPrice && price && intl) {
    try {
      priceLabel =
        price.currency === config.currency
          ? formatMoney(intl, price)
          : `(${price.currency})`;
    } catch (e) {
      priceLabel = '';
    }
  }

  const metadata = listing?.attributes?.metadata || {};
  const listingFieldConfigs = config?.listing?.listingFields || [];
  const categoryConfiguration = config?.categoryConfiguration;

  const accordions = [];
  if (description) {
    accordions.push({
      id: 'description',
      title: 'Description',
      icon: 'description',
      body: description,
    });
  }

  accordions.push(
    ...buildTextFieldAccordions(
      publicData,
      metadata,
      listingFieldConfigs,
      categoryConfiguration
    )
  );

  // About renders after the Details slot (enum/short/video fields). Map covers location.
  const authorProfile = author?.attributes?.profile || {};
  const aboutText = authorProfile.publicData?.uf_about_us_text || authorProfile.bio;
  if (aboutText) {
    accordions.push({
      id: 'about',
      title: authorDisplayName ? `About ${authorDisplayName}` : 'About',
      icon: 'about',
      body: aboutText,
      placement: 'afterDetails',
    });
  }

  const reviewItems = (reviews || []).slice(0, 6).map((review, index) => {
    const rating = review?.attributes?.rating || 0;
    const content = review?.attributes?.content || '';
    const createdAt = review?.attributes?.createdAt;
    const authorName =
      review?.author?.attributes?.profile?.displayName ||
      review?.attributes?.authorName ||
      'Member';
    let timeAgo = '';
    if (createdAt) {
      try {
        timeAgo = new Date(createdAt).toLocaleDateString();
      } catch (e) {
        timeAgo = '';
      }
    }
    return {
      id: review?.id?.uuid || `review-${index}`,
      name: authorName,
      verified: true,
      rating,
      body: content,
      timeAgo,
    };
  });

  const distribution = [0, 0, 0, 0, 0];
  reviewItems.forEach(item => {
    const star = Math.min(5, Math.max(1, Math.round(item.rating)));
    distribution[star - 1] += 1;
  });
  const average =
    reviewItems.length > 0
      ? reviewItems.reduce((sum, item) => sum + item.rating, 0) / reviewItems.length
      : 0;

  return {
    variant,
    breadcrumbs,
    identity: {
      name: memberIdentity.name,
      logoUrl: memberIdentity.logoUrl,
      logoAlt: memberIdentity.logoAlt,
      meta: memberIdentity.meta,
    },
    gallery: {
      items: galleryItems.length
        ? galleryItems
        : [
            {
              id: 'placeholder',
              type: 'image',
              src: PLACEHOLDER_IMAGE,
              thumbSrc: PLACEHOLDER_IMAGE,
              alt: title || 'Listing',
            },
          ],
    },
    // Keep listing title/price available for optional chrome; OrderPanel owns CTA
    listingTitle: title,
    listingPrice: priceLabel,
    accordions,
    moreFrom: null,
    reviews:
      reviewItems.length > 0
        ? {
            average: Math.round(average * 10) / 10,
            count: reviews.length,
            distribution,
            items: reviewItems,
          }
        : null,
  };
};

/**
 * Build listing href for related cards (reserved for future more-from fetch).
 */
export const listingHref = (listing, routeConfiguration) => {
  const id = listing?.id?.uuid;
  const title = listing?.attributes?.title || '';
  if (!id || !routeConfiguration) {
    return '#';
  }
  return pathByRouteName('ListingPage', routeConfiguration, { id, slug: createSlug(title) });
};
